import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { type EmitsClick, type PropsOf } from '@blaze-runtime/engine-domain';
import {
  type InputElementMetaUiFeatures,
  type ToggleButtonStateKey,
} from '@blaze-shared/elements-domain';
import { isDatasetLinkValue } from '@blaze-shared/schema-domain';
import { isNullish, useEmitTracker } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { type CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import {
  booleanToToggleState,
  makeToggleButtonStateStyles,
  toggleStateToBoolean,
} from './toggle-button.utils';

@UntilDestroy()
@Component({
  selector: 'be-toggle-button',
  templateUrl: './toggle-button.component.html',
  styleUrls: ['./toggle-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--toggle-button-element-padding]': 'buttonPadding()',
  },
  imports: [ElementLabelComponent, IconsModule, MatIcon],
})
export class ToggleButtonComponent
  extends CanvasElementBaseComponent<'toggleButton'>
  implements CanvasElementComponent<'toggleButton'>, EmitsClick, OnChanges, OnInit
{
  @Input() value: boolean | undefined;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly schemaUpdateTracker = useEmitTracker();
  private previousDefaultState: ToggleButtonStateKey | undefined;
  private readonly currentProps = signal<PropsOf<'toggleButton'> | undefined>(undefined);
  private readonly stateValue = signal<boolean | undefined>(undefined);
  private readonly defaultStateValue = computed(() =>
    toggleStateToBoolean(this.currentProps()?.content.toggle.defaultState),
  );
  private readonly currentStateValue = computed(
    () => this.stateValue() ?? this.defaultStateValue(),
  );
  private readonly viewState = computed(() => booleanToToggleState(this.currentStateValue()));
  private readonly stateContent = computed(
    () => this.currentProps()?.content.toggle[this.viewState()],
  );
  private readonly stateStyle = computed(
    () => this.currentProps()?.style.states?.[this.viewState()],
  );
  private readonly stateStyles = computed(() => {
    const stateStyle = this.stateStyle();
    if (!stateStyle) {
      return null;
    }
    return makeToggleButtonStateStyles(
      stateStyle,
      this.stateContent()?.iconPosition,
      this.fontsService.getFontWeight(stateStyle.font.fontFamily, stateStyle.font.fontWeight),
      this.isUsingOldPaddingStrategy,
    );
  });

  protected readonly buttonPadding = computed(
    () => this.currentProps()?.style.button.offset.padding ?? '8px',
  );
  protected readonly buttonStyles = computed(() => this.stateStyles()?.buttonStyles ?? null);
  protected readonly labelEnabled = computed(
    () => this.currentProps()?.content.title?.enabled ?? false,
  );
  protected readonly labelText = signal('');
  protected readonly stateIcon = computed(() => this.stateContent()?.icon || '');
  protected readonly stateIconSize = computed(() => this.stateStyle()?.label.iconSize);
  protected readonly stateLabel = computed(() => this.stateContent()?.label || '');
  protected readonly titlePosition = computed(
    () => this.currentProps()?.content.title?.position ?? 'left',
  );
  protected readonly wrapperStyles = computed(() => this.stateStyles()?.wrapperStyles ?? null);

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.button?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      this.currentProps.set(this.props);

      if (this.schemaUpdateTracker.shouldEmit(this.getEventSchema('change'))) {
        this.setDefaultEventSchema('change');
      }

      const defaultState = this.currentProps()?.content.toggle.defaultState;
      const defaultStateChanged = defaultState !== this.previousDefaultState;
      this.previousDefaultState = defaultState;

      // The editor preview seeds from the configured default and re-follows it only when that
      // default is reconfigured — otherwise a builder editing the non-default state would see the
      // canvas snap back on every edit. In the published runtime the user's toggle must survive
      // later props updates.
      if (
        !this.bindingResults?.value &&
        (!this.outputEmitTracker.hasPrevious() || (this.isInEditor && defaultStateChanged))
      ) {
        this.updateOutput(this.defaultStateValue());
      }
    }

    if (changes.value) {
      this.processValue();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText.set(features.title ?? '');
      });
  }

  /**
   * Flips the state and sends the new value out as `change` — the toggle has no `click` output.
   * Named after the `EmitsClick` contract, which the editor toolbar's Trigger button drives;
   * that trigger is the only way to flip the toggle while in the editor.
   */
  emitClick(toolbarTriggered = false): void {
    if (this.isInEditor && !toolbarTriggered) {
      return;
    }
    this.updateOutput(!this.currentStateValue(), true);
  }

  onToggleClick(): void {
    this.emitClick();
  }

  reset(): void {
    this.outputEmitTracker.reset();
    if (this.bindingResults?.['value']) {
      this.processValue();
    } else {
      this.updateOutput(this.defaultStateValue());
    }
  }

  private processValue(): void {
    const rawValue = this.value;
    let normalizedValue = undefined;
    if (isDatasetLinkValue(rawValue)) {
      normalizedValue = Boolean(rawValue.display);
    } else if (isNullish(rawValue)) {
      normalizedValue = rawValue;
    } else {
      normalizedValue = Boolean(rawValue);
    }
    this.updateOutput(normalizedValue);
  }

  private updateOutput(data: boolean | undefined, userUpdate = false): void {
    this.stateValue.set(data);
    // The value is missing, so we don't emit anything
    if (data === undefined) {
      return;
    }

    const schema = this.getEventSchema('change');
    if (!schema) {
      return;
    }

    const isInitialData = !this.outputEmitTracker.hasPrevious();
    if (!this.outputEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data,
      isInitialData,
      schema,
      userUpdate,
    });
  }
}
